import { useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ROUTES = [
  { key: "community", label: "Community / resident", owner: "Community Relations" },
  { key: "commercial", label: "Commercial / partner", owner: "Commercial Development" },
  { key: "investor", label: "Investor / stakeholder", owner: "Investor Relations" },
  { key: "media", label: "Media", owner: "Communications" },
  { key: "careers", label: "Careers question", owner: "Talent Acquisition" },
];

const field = "rounded-none border-white/15 bg-[#061235] h-12 focus-visible:ring-[#D4A24C]";

export const ContactRouting = ({ initial = "community" }) => {
  const [audience, setAudience] = useState(ROUTES.some((r) => r.key === initial) ? initial : "community");
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const route = ROUTES.find((r) => r.key === audience);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, audience });
      setDone(data);
      toast.success(`Inquiry received · ${data?.reference ?? ""}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="inquiry-success" className="hairline chamfer bg-[#0B1D4F] p-8 md:p-12">
        <CheckCircle2 className="h-10 w-10 text-emerald-400" />
        <p className="font-display mt-6 text-4xl font-extrabold uppercase">Received</p>
        <p className="mt-3 text-slate-300">Reference <span data-testid="inquiry-reference" className="font-mono text-white">{done?.reference}</span> · routed to {done?.routed_to}.</p>
        <button data-testid="inquiry-new-button" onClick={() => { setDone(null); setForm({ name: "", email: "", organization: "", message: "" }); }} className="mt-8 border border-white/20 px-5 py-3 text-sm font-semibold hover:border-white">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-routing-form" className="hairline chamfer bg-[#0B1D4F] p-6 md:p-10">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300">1 · Who are you?</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {ROUTES.map((r) => (
          <button type="button" key={r.key} data-testid={`route-${r.key}`} onClick={() => setAudience(r.key)} className={`border px-4 py-2 text-sm transition-colors duration-300 ${audience === r.key ? "border-[#C8102E] bg-[#C8102E] text-white" : "border-white/15 text-slate-200 hover:border-white/50"}`}>{r.label}</button>
        ))}
      </div>
      <p data-testid="route-owner" className="mt-4 font-mono text-xs text-[#E3BC6E]">→ Routed to {route?.owner} [named owner TBD]</p>
      <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-slate-300">2 · Your details</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div><Label htmlFor="c-name">Name</Label><Input id="c-name" required minLength={2} value={form.name} onChange={set("name")} data-testid="contact-name-input" className={field} /></div>
        <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" className={field} /></div>
        <div className="md:col-span-2"><Label htmlFor="c-org">Organization (optional)</Label><Input id="c-org" value={form.organization} onChange={set("organization")} data-testid="contact-org-input" className={field} /></div>
        <div className="md:col-span-2"><Label htmlFor="c-msg">Message</Label><Textarea id="c-msg" required minLength={5} rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" className="rounded-none border-white/15 bg-[#061235] focus-visible:ring-[#D4A24C]" /></div>
      </div>
      <button type="submit" disabled={busy} data-testid="contact-submit-button" className="group mt-8 inline-flex items-center gap-2 bg-[#C8102E] px-6 py-4 font-semibold text-white transition-colors hover:bg-[#E3273F] disabled:opacity-60">
        {busy ? "Sending…" : "Send inquiry"} <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
};
